import {PanelSidebar as Sidebar, SidebarFooter, SidebarHeader, SidebarMenu, SidebarMenuItem, SidebarMenuItemProps, SidebarMenuProps, usePanel} from "../../layout";
import {Link, useRouteMatch} from "react-router-dom";
import React, {FunctionComponent, Key, useMemo} from "react";
import {AccountBoxRounded, ApartmentRounded, OpenInNewRounded} from "@mui/icons-material";
import {Box, Button, Divider, Typography, useTheme} from "@mui/material";
import PanelSidebarDefaultContent from "./default-content";
import {routes} from "../../../../core/models/static/routes/app-routes";
import LazyImage from "../../app-specific/lazy-image";
import useAccessLevels from "../../../hooks/use-access-levels";
import PanelSidebarCustomerContent from "./customer-content";
import PanelSidebarVendorContent from "./vendor-content";
import EnvService from "../../../../core/services/env";
import {useTranslation} from "react-i18next";
import {usePartnerInformation} from "../../../hooks/use-partner-information";
import {useOperatorInformation} from "../../../hooks/use-operator-information";
import CacheService from "../../../../core/services/cache/cache_service";
import {useAppVersion} from "../../../hooks/use-app-version";


export type IPanelSidebarMenuItem =
    ((Omit<SidebarMenuProps, 'children'> & {
        children: Array<IPanelSidebarMenuItem>
    }) | Omit<SidebarMenuItemProps, 'children'>)
    & {
    key: Key;
    hide?: boolean;
};


/**
 * Renders the menus of the sidebar based on the provided menuItem.
 *
 * * This function recurse through the children of the menuItem first and then creates the item on top of it.
 * * If the menuItem has children, it will render a SidebarMenu with the children as its children.
 * * If the menuItem does not have children, it will render a SidebarMenuItem.
 * @param menuItem     The menu item to render.
 * @returns            The rendered menu item.
 */
export const renderPanelSidebarMenu = (menuItem: IPanelSidebarMenuItem) => {
    if ('children' in menuItem && menuItem.children.length) {
        return (
            <SidebarMenu {...menuItem} key={menuItem.key}>
                {renderPanelSidebarMenuItems(menuItem.children)}
            </SidebarMenu>
        );
    }
    return (
        <SidebarMenuItem {...menuItem} key={menuItem.key}/>
    )
}

/**
 * Renders the menu items of the sidebar based on the provided menuItems.
 * @param menuItems     The menu items to render.
 * @returns             The rendered menu items.
 */
export const renderPanelSidebarMenuItems = (menuItems: Array<IPanelSidebarMenuItem>) => {
    return menuItems
            ?.filter((menuItem) => !menuItem.hide)
            ?.map((menuItem) => renderPanelSidebarMenu(menuItem))
        ?? [];
}

interface IPanelSidebarProps {
}

const PanelSidebar: FunctionComponent<IPanelSidebarProps> = () => {
    const panel = usePanel();
    const appVersion = useAppVersion();
    const theme = useTheme();
    const operatorAccessLevels = useAccessLevels();
    const {t} = useTranslation("components", {keyPrefix: 'panel-sections.sidebar'});
    const partnerInformation = usePartnerInformation();
    const operatorInformation = useOperatorInformation();

    const customerViewsMatch = useRouteMatch({path: routes.customer.base, exact: false});
    const vendorViewsMatch = useRouteMatch({path: routes.vendor.base, exact: false});

    const usedPartnerInfo = useMemo(() => ({
        name: EnvService.enableDemoNameMasking ? "BizKey Tech Inc" : partnerInformation?.name ?? "",
        logo: EnvService.enableDemoNameMasking
            ? window.origin.concat("/assets/images/logo.png")
            : (partnerInformation?.configuration?.PartnerLogoUrlBaseAddress ?? "").concat(partnerInformation?.logo ?? ""),
    }), [partnerInformation])

    const usedOperatorInfo = useMemo(() => ({
        name: EnvService.enableDemoNameMasking ? "Demo User" : operatorInformation?.name ?? "",
        image: CacheService.getOperatorProfileImage(operatorInformation?.id),
    }), [operatorInformation])

    return (
        <Sidebar color={"primary"}>
            <SidebarHeader>
                <SidebarMenuItem
                    primaryText={usedPartnerInfo.name}
                    PrimaryTextProps={{fontWeight: 600, sx: {color: 'tertiary.main'}}}
                    {...(operatorAccessLevels.packages.base.editPartnerProfile && {
                        LinkProps: {to: routes.partnerProfile}
                    })}
                    icon={
                        <LazyImage
                            src={usedPartnerInfo.logo}
                            noImage={<ApartmentRounded sx={{width: 30, height: 30,}}/>}
                            className={'panel-image'}
                            wrapperClassName={'panel-image-wrapper'}
                            wrapperProps={{
                                style: {backgroundColor: theme.palette.background.tertiary, borderRadius: 4}
                            }}
                            style={{width: 30, height: 30,}}
                            placeholderSrc={undefined}
                        />
                    }
                />
                <SidebarMenuItem
                    primaryText={usedOperatorInfo.name}
                    ListItemIconProps={{sx: {width: 30, height: 30,}}}
                    {...(operatorAccessLevels.packages.base.editOwnProfile && {
                        LinkProps: {to: routes.operatorProfile}
                    })}
                    icon={
                        //@ts-ignore
                        <LazyImage
                            src={usedOperatorInfo.image}
                            noImage={
                                <AccountBoxRounded
                                    sx={{width: 30, height: 30,}}
                                    color={'inherit'}
                                    className={'sidebar-icon'}
                                />
                            }
                            className={'panel-image'}
                            wrapperClassName={'panel-image-wrapper'}
                            wrapperProps={{
                                style: {borderRadius: 8}
                            }}
                            style={{width: 30, height: 30,}}
                            placeholderSrc={undefined}
                        />
                    }
                />
            </SidebarHeader>
            {
                !!customerViewsMatch &&
                <PanelSidebarCustomerContent/>
            }
            {
                !!vendorViewsMatch &&
                <PanelSidebarVendorContent/>
            }
            {
                !vendorViewsMatch && !customerViewsMatch &&
                <PanelSidebarDefaultContent/>
            }
            <SidebarFooter>
                {
                    EnvService.showInDemoEnvironment &&
                    <Typography
                        variant={panel.sidebarCollapsed ? 'h6' : 'h5'}
                        textAlign={'center'}
                        color={'error.main'}
                        fontWeight={'bold'}
                        sx={{
                            wordBreak: 'break-word',
                        }}
                    >
                        {panel.sidebarCollapsed ? t(t => t.demoEnvironmentSmall) : t(t => t.demoEnvironmentLarge)}
                    </Typography>
                }
                {
                    EnvService.showInMainEnvironment &&
                    <Typography
                        variant={panel.sidebarCollapsed ? 'h6' : 'h5'}
                        textAlign={'center'}
                        color={'error.main'}
                        fontWeight={'bold'}
                        sx={{
                            wordBreak: 'break-word',
                        }}
                    >
                        {panel.sidebarCollapsed ? t(t => t.mainEnvironmentSmall) : t(t => t.mainEnvironmentLarge)}
                    </Typography>
                }
                <Divider key={'divider'} sx={{marginInlineEnd: 2, marginBlockEnd: 1, marginInlineStart: -0.5}}/>
                <Box sx={{display: 'flex', alignItems: "center", flexDirection: 'column'}}>
                    <Typography
                        variant={'caption'}
                        textAlign={'center'}
                        textOverflow={'ellipsis'}
                        noWrap
                    >
                        {panel.sidebarCollapsed ? t(t => t.currentVersionSmall, ({version: appVersion})) : t(t => t.currentVersionLarge, ({version: appVersion}))}
                    </Typography>
                    {
                        operatorAccessLevels.packages.base.versionHistory &&
                        <Button
                            variant={'text'}
                            component={Link as any}
                            to={routes.versions}
                        >
                            <Typography
                                textTransform={'none'}
                                variant={'caption'}
                                textAlign={'center'}
                                textOverflow={'ellipsis'}
                                noWrap
                            >
                                {panel.sidebarCollapsed ? t(t => t.versionHistorySmall) : t(t => t.versionHistoryLarge)}
                            </Typography>
                            <OpenInNewRounded sx={{padding: 0.5}}/>
                        </Button>
                    }
                </Box>
            </SidebarFooter>
        </Sidebar>
    );
};

export default PanelSidebar;
